import React, { useCallback, useContext, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import { AuthContext } from "../../context/AuthContext";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";
import ImageTemplateServices from "../../services/ImageTemplateServices";
import AddImageTemplate from "./components/AddImageTemplate";
import EditImageTemplate from "./components/EditImageTemplate";
import "../../styles/ImageTemplate.css";
import {
  MdAddPhotoAlternate,
  MdEdit,
  MdVisibility,
} from "react-icons/md";
import { CiSearch } from "react-icons/ci";
import { RiCloseLargeFill } from "react-icons/ri";
import { CoverageChips } from "./components/BoardTabs";

const api = new ImageTemplateServices();

const fileBase = () => `${process.env.REACT_APP_NODE_URL || ""}/api/yantra`;

const ImageTemplate = () => {
  const { user } = useContext(AuthContext);
  const rid = Number(user?.role_id);
  const canAccess = rid === 1 || rid === 2;

  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [yantras, setYantras] = useState([]);
  // Running games and their boards, for the per-game tabs in the editor.
  const [games, setGames] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [toggleBusy, setToggleBusy] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [pagination, setPagination] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchFilter(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getList({ page, limit, search: searchFilter });
      if (res.isSuccess) {
        setList(res.data || []);
        setPagination(res.pagination || null);
        if (
          res.pagination &&
          page > res.pagination.totalPages &&
          res.pagination.totalPages >= 1
        ) {
          setPage(res.pagination.totalPages);
        }
      } else {
        toast.error(res.message || "Failed to load");
      }
    } catch (e) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchFilter]);

  const loadYantras = useCallback(async () => {
    try {
      const res = await api.getActiveYantras();
      if (res.isSuccess) {
        setYantras(res.data || []);
        setGames(res.games || []);
      }
    } catch (_) {
      setYantras([]);
      setGames([]);
    }
  }, []);

  useEffect(() => {
    if (!canAccess) return;
    loadList();
    loadYantras();
  }, [canAccess, loadList, loadYantras]);

  const openDetail = async (id) => {
    try {
      const res = await api.getDetail(id);
      if (res.isSuccess) setDetail(res.data);
      else toast.error(res.message || "Failed to load");
    } catch (_) {
      toast.error("Failed to load detail");
    }
  };

  const closeDetail = () => setDetail(null);

  const handleToggle = async (row) => {
    const next = row.is_active === 1 ? 0 : 1;
    setToggleBusy(row.id);
    try {
      const res = await api.toggleActive(row.id, next);
      if (res.isSuccess) {
        toast.success(res.message || "Updated");
        loadList();
      } else {
        toast.error(res.message || "Failed");
      }
    } catch (e) {
      toast.error("Failed to update status");
    } finally {
      setToggleBusy(null);
    }
  };

  if (!canAccess) {
    return (
      <div className="it-page">
        <p className="it-denied">You do not have access to this page.</p>
      </div>
    );
  }

  return (
    <div className="it-page">
      <div className="it-page-header">
        <div className="it-header-bar">
          <div className="it-header-search">
            <div className="it-search-box">
              <CiSearch size={18} className="it-search-icon" />
              <input
                type="text"
                className="it-search-input"
                placeholder="Search image template"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              {searchInput && (
                <button
                  type="button"
                  className="it-search-clear"
                  onClick={() => setSearchInput("")}
                  aria-label="Clear search"
                >
                  <RiCloseLargeFill size={14} />
                </button>
              )}
            </div>
          </div>
          <button
            type="button"
            className="it-add-btn"
            onClick={() => setAddOpen(true)}
            disabled={!yantras.length}
          >
            <MdAddPhotoAlternate size={18} />
            Add Template
          </button>
        </div>
      </div>


      {loading ? (
        <Loader message="Loading templates…" />
      ) : list.length === 0 ? (
        <NoData
          message={
            searchFilter
              ? `No templates match "${searchFilter}"`
              : "No image templates"
          }
          subText={
            searchFilter
              ? "Try a different name or clear the search."
              : "Create a template and upload yantra images."
          }
        />
      ) : (
        <div className="it-table-wrap">
          <table className="it-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Images</th>
                <th>Status</th>
                <th>Created By</th>
                <th>Updated</th>
                <th className="it-th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr key={row.id}>
                  <td className="it-td-name">{row.template_name}</td>
                  <td>
                    <div className="it-coverage">
                      <span className="it-coverage-total">
                        {row.yantra_image_count ?? 0}
                        <small> / {row.yantra_total ?? "—"}</small>
                      </span>
                      <CoverageChips games={row.games} />
                    </div>
                  </td>
                  <td>
                    <label
                      className="it-switch"
                      title={row.is_active === 1 ? "Deactivate" : "Activate"}
                    >
                      <input
                        type="checkbox"
                        checked={row.is_active === 1}
                        disabled={toggleBusy === row.id}
                        onChange={() => handleToggle(row)}
                      />
                      <span className="it-slider" />
                    </label>
                  </td>
                  <td className="it-td-muted">
                    {row.created_by_username || "—"}
                  </td>
                  <td className="it-td-muted">
                    {row.updated_at
                      ? new Date(row.updated_at).toLocaleString()
                      : "—"}
                  </td>
                  <td className="it-td-actions">
                    <button
                      type="button"
                      className="it-icon-act"
                      title="View"
                      onClick={() => openDetail(row.id)}
                    >
                      <MdVisibility size={20} />
                    </button>
                    <button
                      type="button"
                      className="it-icon-act"
                      title="Edit"
                      onClick={() => setEditId(row.id)}
                    >
                      <MdEdit size={20} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="it-cards">
            {list.map((row) => (
              <div key={row.id} className="it-card">
                <div className="it-card-top">
                  <strong className="it-card-title">{row.template_name}</strong>
                  <label
                    className="it-switch"
                    title={row.is_active === 1 ? "Deactivate" : "Activate"}
                  >
                    <input
                      type="checkbox"
                      checked={row.is_active === 1}
                      disabled={toggleBusy === row.id}
                      onChange={() => handleToggle(row)}
                    />
                    <span className="it-slider" />
                  </label>
                </div>
                <div className="it-card-meta">
                  Images: {row.yantra_image_count ?? 0} / {row.yantra_total ?? "—"}
                </div>
                <CoverageChips games={row.games} />
                {row.created_by_username && (
                  <div className="it-card-meta">
                    Created by: {row.created_by_username}
                  </div>
                )}
                <div className="it-card-actions">
                  <button
                    type="button"
                    className="it-btn-secondary"
                    onClick={() => openDetail(row.id)}
                  >
                    View
                  </button>
                  <button
                    type="button"
                    className="it-btn-secondary"
                    onClick={() => setEditId(row.id)}
                  >
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {pagination && pagination.totalPages > 1 && (
        <div className="modern-pagination">
          <div className="mp-limit">
            <span>Show</span>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>

          <div className="mp-controls">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>

            <div className="mp-info">
              Page <strong>{pagination.page}</strong> of{" "}
              <strong>{pagination.totalPages}</strong>
            </div>

            <button
              disabled={page >= pagination.totalPages}
              onClick={() =>
                setPage((p) => Math.min(pagination.totalPages, p + 1))
              }
            >
              Next
            </button>
          </div>

          <div className="mp-jump">
            <select
              value={page}
              onChange={(e) => setPage(Number(e.target.value))}
            >
              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  Page {i + 1}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <AddImageTemplate
        show={addOpen}
        onHide={() => setAddOpen(false)}
        yantras={yantras}
        games={games}
        onSaved={loadList}
      />

      <EditImageTemplate
        show={!!editId}
        templateId={editId}
        onHide={() => setEditId(null)}
        yantras={yantras}
        games={games}
        onSaved={loadList}
      />

      <Modal show={!!detail} onHide={closeDetail} size="lg" centered scrollable>
        <div className="it-modal-head">
          <h5 className="it-modal-title">
            {detail?.template?.template_name || "Template detail"}
          </h5>
          <button
            type="button"
            className="it-icon-close"
            onClick={closeDetail}
            aria-label="Close"
          >
            <RiCloseLargeFill size={20} />
          </button>
        </div>
        <Modal.Body className="it-modal-body">
          {detail && (
            <>
              <div className="it-detail-meta">
                <span
                  className={
                    detail.template?.is_active === 1
                      ? "it-badge on"
                      : "it-badge off"
                  }
                >
                  {detail.template?.is_active === 1 ? "Active" : "Inactive"}
                </span>
              </div>
              <div className="it-detail-grid">
                {(detail.items || []).map((it, idx) => (
                  <div key={it.yantra_id} className="it-detail-cell">
                    <div className="it-detail-label">
                      {it.yantra_name}{" "}
                      <span className="it-yantra-id">(# {idx + 1})</span>
                    </div>
                    {it.image_filename ? (
                      <img
                        src={`${fileBase()}/${encodeURIComponent(it.image_filename)}`}
                        alt={it.yantra_name}
                        className="it-detail-img"
                      />
                    ) : (
                      <div className="it-detail-empty">No image</div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ImageTemplate;
